import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Container,
  Row,
  Col,
  Form,
  Button,
  ButtonGroup,
  OverlayTrigger,
  Tooltip,
  Modal,
  Offcanvas,
  Accordion,
  Badge,
  Spinner,
} from "react-bootstrap";
import { getUserPracticeById } from "../../api";
import { getAgeAnalysis, getAgeAnalysisExport } from "../../afinanceapi";
import { jwtDecode } from "jwt-decode";
import { useLocation, useNavigate } from "react-router-dom";
import { unparse } from "papaparse";
import "./CreateInvoice.css"; // Assuming you have some styles in this file

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function AgedInvoicesList() {
  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totals, setTotals] = useState({
    sumPending: 0,
    sum30: 0,
    sum60: 0,
    sum90: 0,
    sum120: 0,
    sumAll: 0,
  });
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Data for dropdowns
  const [practiceList, setPracticeList] = useState([]);

  // Committed filters (drive the table)
  const [selectedPractices, setSelectedPractices] = useState([]); // [] = All
  const [selectedStatuses, setSelectedStatuses] = useState([]); // [] = All
  const [selectedAging, setSelectedAging] = useState([]); // [], e.g. ["30","60"]

  // Temporary filters while panel is open
  const [tmpPractices, setTmpPractices] = useState([]);
  const [tmpStatuses, setTmpStatuses] = useState([]);
  const [tmpAging, setTmpAging] = useState([]);
  const [userRole, setUserRole] = useState(false); // True if user has allowed role

  // Grouping
  const [groupedView, setGroupedView] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // Set of accountName strings

  // Row accordion expansion
  const [expandedRows, setExpandedRows] = useState(new Set()); // Set of invoice _id strings

  // Export loading
  const [exportLoading, setExportLoading] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const limit = 20;

  // UI
  const [showInfo, setShowInfo] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();
  const agingParam = query.get("aging");

  const currency = new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
  });

  // Hardcoded status options (no longer derived from dataset)
  const statusOptions = ["Partially Paid", "Pending", "Posted"];

  // Load practice list and user role once
  useEffect(() => {
    async function loadPractices() {
      try {
        const token = sessionStorage.getItem("user");
        if (!token) return;
        const user = jwtDecode(token);
        const rolesFromRole = Array.isArray(user?.role)
          ? user.role
          : user?.role
            ? [user.role]
            : [];
        const rolesFromPermissions = Array.isArray(user?.permissions)
          ? user.permissions
          : [];
        const roles = [...new Set([...rolesFromRole, ...rolesFromPermissions])];
        const allowedRoles = ["Practitioner", "Finance", "SuperAdmin"];
        setUserRole(roles.some((r) => allowedRoles.includes(r)));
        const practices = await getUserPracticeById(user._id);
        setPracticeList(practices || []);
      } catch (e) {
        console.error("Error loading practices:", e);
      }
    }
    loadPractices();
  }, []);

  // Backend-driven data fetch — fires on every filter/sort/page change
  useEffect(() => {
    if (practiceList.length === 0) return;

    async function fetchData() {
      try {
        setLoading(true);
        const practiceNumbers = practiceList.map((p) => p.practiceNumber);
        const result = await getAgeAnalysis({
          practiceNumbers,
          statuses: selectedStatuses,
          aging: selectedAging,
          practices: selectedPractices,
          sortKey: sortConfig.key,
          sortDir: sortConfig.direction,
          page: groupedView ? 1 : page,
          limit: groupedView ? 10000 : limit,
        });
        setInvoices(result.data || []);
        setTotalCount(result.total || 0);
        setTotals(
          result.totals || {
            sumPending: 0,
            sum30: 0,
            sum60: 0,
            sum90: 0,
            sum120: 0,
            sumAll: 0,
          },
        );
        setExpandedGroups(new Set());
        setExpandedRows(new Set());
      } catch (e) {
        console.error("Error fetching age analysis:", e);
        setInvoices([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [
    practiceList,
    selectedStatuses,
    selectedAging,
    selectedPractices,
    sortConfig,
    page,
    groupedView,
  ]);

  // Read URL params on mount to initialise committed filter state (per D-04)
  // Handles: practices, statuses, aging (unified — replaces aging-only useEffect)
  useEffect(() => {
    const p = query.get('practices');
    const s = query.get('statuses');
    const a = query.get('aging');
    if (p) { const arr = p.split(','); setSelectedPractices(arr); setTmpPractices(arr); }
    if (s) { const arr = s.split(','); setSelectedStatuses(arr); setTmpStatuses(arr); }
    if (a) {
      const arr = a.split(',').filter(v => ['30','60','90','120'].includes(v));
      if (arr.length) { setSelectedAging(arr); setTmpAging(arr); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helpers for multi-select toggles
  const toggleIn = (arr, value) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  const allPractices = useMemo(
    () => (practiceList || []).map((p) => p.practiceName).filter(Boolean),
    [practiceList],
  );

  // Pagination
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [selectedPractices, selectedStatuses, selectedAging]);

  // Grouping computation
  const groupedInvoices = useMemo(() => {
    if (!groupedView) return [];
    const map = new Map();
    for (const inv of invoices) {
      const key = inv.accountName || "(No Account)";
      if (!map.has(key)) {
        map.set(key, {
          accountName: key,
          accountId: inv.accountId,
          accountNumber: inv.accountNumber || "",
          invoices: [],
          sum30: 0,
          sum60: 0,
          sum90: 0,
          sum120: 0,
          sumPending: 0,
        });
      }
      const g = map.get(key);
      g.invoices.push(inv);
      g.sum30 += inv.days30 || 0;
      g.sum60 += inv.days60 || 0;
      g.sum90 += inv.days90 || 0;
      g.sum120 += inv.days120plus || 0;
      g.sumPending += inv.pending || 0;
    }
    return Array.from(map.values());
  }, [invoices, groupedView]);

  const toggleGroup = (accountName) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(accountName) ? next.delete(accountName) : next.add(accountName);
      return next;
    });
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    setSortConfig({ key, direction });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <i className="fa-solid fa-arrow-up text-primary ms-2" />
    ) : (
      <i className="fa-solid fa-arrow-down text-primary ms-2" />
    );
  };

  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      const practiceNumbers = practiceList.map((p) => p.practiceNumber);
      const allInvoices = await getAgeAnalysisExport({
        practiceNumbers,
        statuses: selectedStatuses,
        aging: selectedAging,
        practices: selectedPractices,
        sortKey: sortConfig.key,
        sortDir: sortConfig.direction,
      });

      const exportData = allInvoices.reduce((acc, inv) => {
        const invDate = inv.invoiceDate ? new Date(inv.invoiceDate).toISOString().slice(0, 10) : "";
        const practiceName = inv.practice?.practiceName || "";

        // One row per invoice line
        (inv.lines || []).forEach((line) => {
          const sp = line.serviceProvider
            ? `${line.serviceProvider.firstName} ${line.serviceProvider.lastName}`.trim()
            : inv.practitioner?.firstName
              ? `${inv.practitioner.firstName} ${inv.practitioner.lastName || ""}`.trim()
              : "";
          acc.push({
            InvoiceDate: invDate,
            InvoiceNumber: inv.invoiceNumber || "",
            AccountName: inv.accountName || "",
            Status: inv.status || "",
            TariffCode: line.tariffCode || line.nappiCode || "",
            Description: line.description || "",
            Quantity: line.quantity ?? 1,
            Rate: line.rate || 0,
            CN_WO_Number: "",
            CN_WO_Type: "",
            CN_WO_Amount: "",
            ServiceProvider: sp,
            PracticeName: practiceName,
            Pending: inv.pending || 0,
            Days30: inv.days30 || 0,
            Days60: inv.days60 || 0,
            Days90: inv.days90 || 0,
            Days120plus: inv.days120plus || 0,
          });
        });

        // One row per credit note
        (inv.receipts || []).filter((r) => r.creditNoteNumber).forEach((r) => {
          acc.push({
            InvoiceDate: r.creditNoteDate ? new Date(r.creditNoteDate).toISOString().slice(0, 10) : invDate,
            InvoiceNumber: inv.invoiceNumber || "",
            AccountName: inv.accountName || "",
            Status: inv.status || "",
            TariffCode: "",
            Description: `Credit Note ${r.creditNoteNumber || ""}`,
            Quantity: "",
            Rate: -(r.creditNoteAmount || 0),
            ServiceProvider: "",
            PracticeName: practiceName,
            Pending: "",
            Days30: "",
            Days60: "",
            Days90: "",
            Days120plus: "",
          });
        });

        // One row per write-off
        (inv.receipts || []).filter((r) => r.writeOffNumber).forEach((r) => {
          acc.push({
            InvoiceDate: r.writeOffDate ? new Date(r.writeOffDate).toISOString().slice(0, 10) : invDate,
            InvoiceNumber: inv.invoiceNumber || "",
            AccountName: inv.accountName || "",
            Status: inv.status || "",
            TariffCode: "",
            Description: `Write Off ${r.writeOffNumber || ""}`,
            Quantity: "",
            Rate: -(r.writeOffAmount || 0),
            ServiceProvider: "",
            PracticeName: practiceName,
            Pending: "",
            Days30: "",
            Days60: "",
            Days90: "",
            Days120plus: "",
          });
        });

        return acc;
      }, []);

      const csv = unparse(exportData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "aged-invoices.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Export failed:", e);
    } finally {
      setExportLoading(false);
    }
  };

  // Open filter panel with current committed values loaded into temp
  const openFilters = () => {
    setTmpPractices(selectedPractices);
    setTmpStatuses(selectedStatuses);
    setTmpAging(selectedAging);
    setShowFilters(true);
  };

  const applyFilters = () => {
    setSelectedPractices(tmpPractices);
    setSelectedStatuses(tmpStatuses);
    setSelectedAging(tmpAging);
    setShowFilters(false);

    // Write filters to URL — preserve existing params (e.g. section=) then set/clear filter keys
    const params = new URLSearchParams(location.search);
    params.delete('practices'); params.delete('statuses'); params.delete('aging');
    if (tmpPractices.length) params.set('practices', tmpPractices.join(','));
    if (tmpStatuses.length) params.set('statuses', tmpStatuses.join(','));
    if (tmpAging.length) params.set('aging', tmpAging.join(','));
    navigate({ search: params.toString() }, { replace: true });
  };

  const activeCount =
    (selectedPractices.length ? 1 : 0) +
    (selectedStatuses.length ? 1 : 0) +
    (selectedAging.length ? 1 : 0);

  return (
    <Container fluid className="pt-2">
      {/* Single top bar: Filter + Remove + Pagination + CSV + Share/Info (right-aligned) */}
      <Row className="mb-3 align-items-center">
        {/* Right Column: Totals Table */}
        <Col md={6}>
          <Form.Label className="fw-semibold text-primary">
            Totals (all filtered rows)
          </Form.Label>
          <Table bordered size="sm" className="mb-0 text-center align-middle">
            <thead className="table-primary">
              <tr>
                <th
                  style={{ width: "16.66%" }}
                  className="fw-semibold text-primary"
                >
                  Pending
                </th>
                <th
                  style={{ width: "16.66%" }}
                  className="fw-semibold text-primary"
                >
                  30 days
                </th>
                <th
                  style={{ width: "16.66%" }}
                  className="fw-semibold text-primary"
                >
                  60 days
                </th>
                <th
                  style={{ width: "16.66%" }}
                  className="fw-semibold text-primary"
                >
                  90 days
                </th>
                <th
                  style={{ width: "16.66%" }}
                  className="fw-semibold text-primary"
                >
                  120 days+
                </th>
                <th
                  style={{ width: "16.66%" }}
                  className="fw-semibold text-primary"
                >
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{currency.format(totals.sumPending)}</td>
                <td>{currency.format(totals.sum30)}</td>
                <td>{currency.format(totals.sum60)}</td>
                <td>{currency.format(totals.sum90)}</td>
                <td>{currency.format(totals.sum120)}</td>
                <td>
                  <strong>{currency.format(totals.sumAll)}</strong>
                </td>
              </tr>
            </tbody>
          </Table>
        </Col>
        {/* Left Column: Filter + Pagination + Actions */}
        <Col md={6} className="d-flex gap-2 flex-wrap justify-content-end">
          <Button
            variant="primary"
            onClick={openFilters}
            style={{ width: "120px" }}
          >
            <i className="fa-solid fa-filter me-2" />
            Filter{" "}
            {activeCount > 0 && (
              <Badge className="ms-2" bg="light" text="dark">
                {activeCount}
              </Badge>
            )}
          </Button>
         {/*} <Button
            variant="outline-danger"
            onClick={clearFilters}
            style={{ width: "120px" }}
            disabled={
              selectedPractices.length === 0 &&
              selectedStatuses.length === 0 &&
              selectedAging.length === 0
            }
          >
            <i className="fa-solid fa-trash me-2" />
            Filters
          </Button> */}

          {/* Grouping toggle */}
          <Button
            variant={groupedView ? "primary" : "outline-primary"}
            onClick={() => setGroupedView((v) => !v)}
            style={{ width: "120px" }}
            title={
              groupedView ? "Switch to flat list" : "Switch to grouped view"
            }
          >
            <i
              className={`fa-solid ${groupedView ? "fa-list" : "fa-layer-group"} me-2`}
            />
            {groupedView ? "Flatten" : "Group"}
          </Button>

          {/* Pagination */}
          <div className="d-flex align-items-center gap-2 ms-2">
            <Button
              variant="primary"
              disabled={groupedView || page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <i className="fa-solid fa-angle-left" />
            </Button>
            <div className="text-nowrap small">
              {groupedView ? "Page 1 of 1" : `Page ${page} of ${totalPages}`}
            </div>
            <Button
              variant="primary"
              disabled={groupedView || page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <i className="fa-solid fa-angle-right" />
            </Button>
          </div>

          {/* Share + Info */}
          <ButtonGroup className="ms-1 gap-1">
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip>Export to CSV</Tooltip>}
            >
              <Button
                variant="outline-secondary"
                onClick={handleExportCSV}
                disabled={exportLoading}
              >
                {exportLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <i className="fa-solid fa-file-export" />
                )}
              </Button>
            </OverlayTrigger>

            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip>Info</Tooltip>}
            >
              <Button
                variant="outline-secondary"
                onClick={() => setShowInfo(true)}
              >
                <i className="fa-solid fa-circle-info" />
              </Button>
            </OverlayTrigger>
          </ButtonGroup>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading Invoices...</p>
        </div>
      ) : invoices.length === 0 ? (
        <p>No invoices found.</p>
      ) : (
        <>
          <Form.Label className="fw-semibold text-primary">
            Click on the Invoice Number to navigate to the Invoice and the
            Account Name to navigate to the Account.
          </Form.Label>

          {groupedView ? (
            <Table bordered hover responsive className="fixed-table">
              <thead className="table-primary text-center align-middle">
                <tr>
                  <th style={{ width: "2%" }}></th>
                  <th
                    style={{ width: "6%" }}
                    className="text-primary fw-semibold"
                  >
                    Acc #
                  </th>
                  <th
                    style={{ width: "23%" }}
                    className="text-primary fw-semibold"
                  >
                    Account Holder
                  </th>
                  <th
                    style={{ width: "6%" }}
                    className="text-primary fw-semibold"
                  >
                    Pending
                  </th>
                  <th
                    style={{ width: "6%" }}
                    className="text-primary fw-semibold"
                  >
                    30 D
                  </th>
                  <th
                    style={{ width: "6%" }}
                    className="text-primary fw-semibold"
                  >
                    60 D
                  </th>
                  <th
                    style={{ width: "6%" }}
                    className="text-primary fw-semibold"
                  >
                    90 D
                  </th>
                  <th
                    style={{ width: "6%" }}
                    className="text-primary fw-semibold"
                  >
                    120+ D
                  </th>
                  <th
                    style={{ width: "6%" }}
                    className="text-primary fw-semibold"
                  >
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {groupedInvoices.map((group) => {
                  const isExpanded = expandedGroups.has(group.accountName);
                  const groupTotal =
                    group.sum30 + group.sum60 + group.sum90 + group.sum120;
                  return (
                    <React.Fragment key={group.accountName}>
                      {/* Account holder header row */}
                      <tr
                        style={{
                          cursor: "pointer",
                          backgroundColor: "#e8f0fe",
                        }}
                        onClick={() => toggleGroup(group.accountName)}
                      >
                        <td className="text-center align-middle">
                          <i
                            className={`fa-solid fa-chevron-${isExpanded ? "down" : "right"} text-primary`}
                          />
                        </td>
                        <td>
                          {group.accountNumber}
                        </td>
                        <td>
                          {group.accountName}
                        </td>
                        <td className="text-end">
                          {group.sumPending > 0
                            ? currency.format(group.sumPending)
                            : ""}
                        </td>
                        <td className="text-end">
                          {group.sum30 > 0 ? currency.format(group.sum30) : ""}
                        </td>
                        <td className="text-end">
                          {group.sum60 > 0 ? currency.format(group.sum60) : ""}
                        </td>
                        <td className="text-end">
                          {group.sum90 > 0 ? currency.format(group.sum90) : ""}
                        </td>
                        <td className="text-end">
                          {group.sum120 > 0
                            ? currency.format(group.sum120)
                            : ""}
                        </td>
                        <td className="text-end fw-bold">
                          {groupTotal > 0 ? currency.format(groupTotal) : ""}
                        </td>
                      </tr>
                      {/* Collapsible invoice rows */}
                      {isExpanded &&
                        group.invoices.map((inv) => {
                          const creditNotes = (inv.receipts || []).filter((r) => r.creditNoteNumber);
                          const writeOffs = (inv.receipts || []).filter((r) => r.writeOffNumber);
                          const totalCN = creditNotes.reduce((s, r) => s + (r.creditNoteAmount || 0), 0);
                          const totalWO = writeOffs.reduce((s, r) => s + (r.writeOffAmount || 0), 0);
                          const adjustedTotal = (inv.total || 0) - totalCN - totalWO;
                          return (
                          <React.Fragment key={inv._id}>
                            <tr
                              style={{
                                cursor: "pointer",
                                "--bs-table-bg": expandedRows.has(inv._id)
                          ? "#CFE3FF4F"
                          : "",
                        "--bs-table-color": expandedRows.has(inv._id)
                          ? "#0f3490"
                          : "",
                        ...(expandedRows.has(inv._id)
                          ? {
                              color: "#cfe2ff",
                              borderBottom: "1px solid #0f3490",
                              fontWeight: 500,
                            }
                                  : {}),
                              }}
                              onClick={() => toggleRow(inv._id)}
                            >
                              <td></td>
                              <td></td>
                              <td>
                                <i
                                  className={`fa-solid fa-chevron-${expandedRows.has(inv._id) ? "down" : "right"} text-primary me-2`}
                                />
                                <span
                                  style={{
                                    cursor: "pointer",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(
                                      inv.status === "Pending"
                                        ? `/invoices/${inv._id}/${inv.accountId}`
                                        : `/invoices/posted/${inv._id}`,
                                      inv.status !== "Pending"
                                        ? { state: { breadcrumbTrail: [{ label: 'Invoice List', path: `${location.pathname}${location.search}` }] } }
                                        : undefined
                                    );
                                  }}
                                >
                                  Inv #: {inv.invoiceNumber}
                                </span>
                                <span className="ms-2">
                                  {inv.invoiceDate
                                    ? new Date(
                                        inv.invoiceDate,
                                      ).toLocaleDateString()
                                    : ""}
                                </span>
                                <span className="ms-2">
                                  {inv.status}
                                </span>
                              </td>
                              <td className="text-end">
                                {inv.pending > 0
                                  ? currency.format(inv.pending)
                                  : ""}
                              </td>
                              <td className="text-end">
                                {inv.days30 > 0
                                  ? currency.format(inv.days30)
                                  : ""}
                              </td>
                              <td className="text-end">
                                {inv.days60 > 0
                                  ? currency.format(inv.days60)
                                  : ""}
                              </td>
                              <td className="text-end">
                                {inv.days90 > 0
                                  ? currency.format(inv.days90)
                                  : ""}
                              </td>
                              <td className="text-end">
                                {inv.days120plus > 0
                                  ? currency.format(inv.days120plus)
                                  : ""}
                              </td>
                              <td className="text-end fw-bold">
                                {adjustedTotal > 0 ? currency.format(adjustedTotal) : ""}
                              </td>
                            </tr>
                            {expandedRows.has(inv._id) && (
                              <tr>
                                <td colSpan={2}></td>
                                <td
                                  colSpan={7}
                                  style={{ padding: "0.25rem 0.5rem" }}
                                >
                                  {!inv.lines || inv.lines.length === 0 ? (
                                    <span className="text-muted small">
                                      No line items
                                    </span>
                                  ) : (
                                    <table
                                      className="table table-sm table-borderless mb-0"
                                      style={{
                                        fontSize: "0.85rem",
                                        tableLayout: "fixed",
                                        width: "100%",
                                      }}
                                    >
                                      <colgroup>
                                        <col style={{ width: "11%" }} />
                                        <col style={{ width: "40%" }} />
                                        <col style={{ width: "19%" }} />
                                        <col style={{ width: "5%" }} />
                                        <col style={{ width: "10%" }} />
                                      </colgroup>
                                      <tbody>
                                        {inv.lines.map((line, li) => (
                                          <tr key={li}>
                                            <td className="text-primary">{line.tariffCode || "-"}</td>
                                            <td className="text-primary">{line.description || "-"}</td>
                                            <td className="text-primary">
                                              {line.serviceProvider
                                                ? `${line.serviceProvider.firstName} ${line.serviceProvider.lastName}`.trim()
                                                : inv.practitioner?.firstName
                                                  ? `${inv.practitioner.firstName} ${inv.practitioner.lastName || ""}`.trim()
                                                  : "-"}
                                            </td>
                                            <td className="text-center text-primary">
                                              {line.quantity ?? 1}
                                            </td>
                                            <td className="text-end text-primary">
                                              {currency.format(line.rate || 0)}
                                            </td>
                                          </tr>
                                        ))}
                                        {creditNotes.map((r, ci) => (
                                          <tr key={`cn-${ci}`} style={{ color: "#6c757d" }}>
                                            <td colSpan={3} className="text-danger" style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                                              Credit Note {r.creditNoteNumber}{r.creditNoteDate ? ` — ${new Date(r.creditNoteDate).toLocaleDateString()}` : ""}
                                            </td>
                                            <td colSpan={1}></td>
                                            <td className="text-end text-danger">({currency.format(r.creditNoteAmount || 0)})</td>
                                          </tr>
                                        ))}
                                        {writeOffs.map((r, wi) => (
                                          <tr key={`wo-${wi}`} style={{ color: "#6c757d" }}>
                                            <td colSpan={3} style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                                              Write Off {r.writeOffNumber}{r.writeOffDate ? ` — ${new Date(r.writeOffDate).toLocaleDateString()}` : ""}
                                            </td>
                                            <td colSpan={1}></td>
                                            <td className="text-end text-danger">({currency.format(r.writeOffAmount || 0)})</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          ) : (
            <Table bordered hover responsive className="fixed-table">
              <thead className="table-primary text-center align-middle ms-1 me-1">
                <tr>
                  <th style={{ width: "3%" }}></th>
                  <th
                    style={{ width: "8%" }}
                    className="text-primary fw-semibold"
                  >
                    Date
                  </th>
                  <th
                    style={{ width: "9%" }}
                    className="text-primary fw-semibold"
                  >
                    Inv Number
                  </th>
                  <th
                    style={{ width: "14%" }}
                    className="text-primary fw-semibold"
                  >
                    Practice Name
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "14%" }}
                    onClick={() => handleSort("accountName")}
                    className="text-primary fw-semibold"
                  >
                    Account Name {renderSortIcon("accountName")}
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "10%" }}
                    onClick={() => handleSort("status")}
                    className="text-primary fw-semibold"
                  >
                    Status {renderSortIcon("status")}
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "8%" }}
                    onClick={() => handleSort("pending")}
                    className="text-primary fw-semibold"
                  >
                    Pend {renderSortIcon("pending")}
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "8%" }}
                    onClick={() => handleSort("days30")}
                    className="text-primary fw-semibold"
                  >
                    30 D {renderSortIcon("days30")}
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "8%" }}
                    onClick={() => handleSort("days60")}
                    className="text-primary fw-semibold"
                  >
                    60 D {renderSortIcon("days60")}
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "8%" }}
                    onClick={() => handleSort("days90")}
                    className="text-primary fw-semibold"
                  >
                    90 D {renderSortIcon("days90")}
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "8%" }}
                    onClick={() => handleSort("days120plus")}
                    className="text-primary fw-semibold"
                  >
                    120+ D {renderSortIcon("days120plus")}
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "8%" }}
                    onClick={() => handleSort("total")}
                    className="text-primary fw-semibold"
                  >
                    Total {renderSortIcon("total")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const creditNotes = (inv.receipts || []).filter((r) => r.creditNoteNumber);
                  const writeOffs = (inv.receipts || []).filter((r) => r.writeOffNumber);
                  const totalCN = creditNotes.reduce((s, r) => s + (r.creditNoteAmount || 0), 0);
                  const totalWO = writeOffs.reduce((s, r) => s + (r.writeOffAmount || 0), 0);
                  const adjustedTotal = (inv.total || 0) - totalCN - totalWO;
                  return (
                  <React.Fragment key={inv._id}>
                    <tr
                      style={{
                        cursor: "pointer",
                        "--bs-table-bg": expandedRows.has(inv._id)
                          ? "#CFE3FF4F"
                          : "",
                        "--bs-table-color": expandedRows.has(inv._id)
                          ? "#0f3490"
                          : "",
                        ...(expandedRows.has(inv._id)
                          ? {
                              color: "#cfe2ff",
                              borderTop: "1px solid #0f3490",
                              fontWeight: 500,
                            }
                          : {}),
                      }}
                      onClick={() => toggleRow(inv._id)}
                    >
                      <td className="text-center align-middle">
                        <i
                          className={`fa-solid fa-chevron-${expandedRows.has(inv._id) ? "down" : "right"} text-primary`}
                        />
                      </td>
                      <td>
                        {inv.invoiceDate
                          ? new Date(inv.invoiceDate).toLocaleDateString()
                          : ""}
                      </td>
                      <td
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            inv.status === "Pending"
                              ? `/invoices/${inv._id}/${inv.accountId}`
                              : `/invoices/posted/${inv._id}`,
                            inv.status !== "Pending"
                              ? { state: { breadcrumbTrail: [{ label: 'Invoice List', path: `${location.pathname}${location.search}` }] } }
                              : undefined
                          );
                        }}
                      >
                        {inv.invoiceNumber}
                      </td>
                      <td>{inv.practice?.practiceName}</td>
                      <td
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/accounts/${inv.accountId}`, {
                            state: { breadcrumbTrail: [{ label: 'Invoice List', path: `${location.pathname}${location.search}` }] },
                          });
                        }}
                      >
                        {inv.accountName}
                      </td>
                      <td>{inv.status}</td>
                      <td className="text-end">
                        {inv.pending > 0 ? currency.format(inv.pending) : ""}
                      </td>
                      <td className="text-end">
                        {inv.days30 > 0 ? currency.format(inv.days30) : ""}
                      </td>
                      <td className="text-end">
                        {inv.days60 > 0 ? currency.format(inv.days60) : ""}
                      </td>
                      <td className="text-end">
                        {inv.days90 > 0 ? currency.format(inv.days90) : ""}
                      </td>
                      <td className="text-end">
                        {inv.days120plus > 0
                          ? currency.format(inv.days120plus)
                          : ""}
                      </td>
                      <td className="text-end fw-bold">
                        {adjustedTotal > 0 ? currency.format(adjustedTotal) : ""}
                      </td>
                    </tr>
                    {expandedRows.has(inv._id) && (
                      <tr key={`${inv._id}-lines`}>
                        <td colSpan={2}></td>
                        <td colSpan={10} style={{ padding: "0.25rem 0.5rem" }}>
                          {!inv.lines || inv.lines.length === 0 ? (
                            <span className="text-muted small">
                              No line items
                            </span>
                          ) : (
                            <table
                              className="table table-sm table-borderless mb-0"
                              style={{
                                fontSize: "0.85rem",
                                tableLayout: "fixed",
                                width: "100%",
                              }}
                            >
                              <colgroup>
                                <col style={{ width: "10%" }} />
                                <col style={{ width: "45%" }} />
                                <col style={{ width: "20%" }} />
                                <col style={{ width: "5%" }} />
                                <col style={{ width: "10%" }} />
                              </colgroup>
                              <tbody>
                                {inv.lines.map((line, li) => (
                                  <tr key={li}>
                                    <td className="text-primary">{line.tariffCode || line.nappiCode || "-"}</td>
                                    <td className="text-primary">{line.description || "-"}</td>
                                    <td className="text-primary">
                                      {line.serviceProvider
                                        ? `${line.serviceProvider.firstName} ${line.serviceProvider.lastName}`.trim()
                                        : inv.practitioner?.firstName
                                          ? `${inv.practitioner.firstName} ${inv.practitioner.lastName || ""}`.trim()
                                          : "-"}
                                    </td>
                                    <td className="text-center text-primary">
                                      {line.quantity ?? 1}
                                    </td>
                                    <td className="text-end text-primary">
                                      {currency.format(line.rate || 0)}
                                    </td>
                                  </tr>
                                ))}
                                {creditNotes.map((r, ci) => (
                                  <tr key={`cn-${ci}`} style={{ color: "#6c757d" }}>
                                    <td colSpan={3} className="text-danger" style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                                      Credit Note {r.creditNoteNumber}{r.creditNoteDate ? ` — ${new Date(r.creditNoteDate).toLocaleDateString()}` : ""}
                                    </td>
                                    <td colSpan={1}></td>
                                    <td className="text-end text-danger">({currency.format(r.creditNoteAmount || 0)})</td>
                                  </tr>
                                ))}
                                {writeOffs.map((r, wi) => (
                                  <tr key={`wo-${wi}`} style={{ color: "#6c757d" }}>
                                    <td colSpan={3} className="text-danger" style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                                      Write Off {r.writeOffNumber}{r.writeOffDate ? ` — ${new Date(r.writeOffDate).toLocaleDateString()}` : ""}
                                    </td>
                                    <td colSpan={1}></td>
                                    <td className="text-end text-danger">({currency.format(r.writeOffAmount || 0)})</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          )}
        </>
      )}

      {/* Info modal */}
      <Modal show={showInfo} onHide={() => setShowInfo(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Aged Invoices</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-muted">
          Use the filter panel to refine the dataset. Totals reflect all
          filtered rows (not just this page). CSV export downloads the entire
          filtered dataset.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowInfo(false)}>
            Got it
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Filter Offcanvas (unchanged) */}
      <Offcanvas
        show={showFilters}
        onHide={() => setShowFilters(false)}
        placement="start"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="test-primary d-flex align-items-center">
            <i className="fa-solid fa-filter me-2 text-primary" />
            Filters
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Accordion alwaysOpen>
            {/* Practices */}
            <Accordion.Item eventKey="0">
              <Accordion.Header>Practices</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpPractices(allPractices)}
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpPractices([])}
                  >
                    Clear
                  </Button>
                </div>
                <div
                  className="d-flex flex-column"
                  style={{ maxHeight: 240, overflowY: "auto" }}
                >
                  {allPractices.map((name) => (
                    <Form.Check
                      key={name}
                      type="checkbox"
                      id={`practice-${name}`}
                      label={name}
                      checked={tmpPractices.includes(name)}
                      onChange={() =>
                        setTmpPractices((arr) => toggleIn(arr, name))
                      }
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>

            {/* Statuses */}
            <Accordion.Item eventKey="1">
              <Accordion.Header>Status</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpStatuses(statusOptions)}
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpStatuses([])}
                  >
                    Clear
                  </Button>
                </div>
                <div className="d-flex flex-column">
                  {statusOptions.map((s) => (
                    <Form.Check
                      key={s}
                      type="checkbox"
                      id={`status-${s}`}
                      label={s}
                      checked={tmpStatuses.includes(s)}
                      onChange={() => setTmpStatuses((arr) => toggleIn(arr, s))}
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>

            {/* Aging */}
            <Accordion.Item eventKey="2">
              <Accordion.Header>Aging</Accordion.Header>
              <Accordion.Body>
                <div className="d-flex flex-column">
                  {["30", "60", "90", "120"].map((a) => (
                    <Form.Check
                      key={a}
                      type="checkbox"
                      id={`aging-${a}`}
                      label={`${a} Days${a === "120" ? "+" : ""}`}
                      checked={tmpAging.includes(a)}
                      onChange={() => setTmpAging((arr) => toggleIn(arr, a))}
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* Actions */}
          <div className="mt-4 d-flex gap-2">
            <Button onClick={applyFilters} style={{ width: "120px" }}>
              <i className="fa-solid fa-filter me-2 text-white" />
              Apply
            </Button>
            <Button
              style={{ width: "120px" }}
              variant="outline-danger"
              onClick={() => {
                setTmpPractices([]);
                setTmpStatuses([]);
                setTmpAging([]);
              }}
            >
              <i className="fa-solid fa-trash me-2" />
              Remove
            </Button>
          </div>
          <div className="mt-2 text-muted small">
            Tip: leaving a section empty means "All".
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}
